import * as React from "react";
import { Upload, X } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";
import { addStairs, addSurface, addSurfaces } from "../../services/api";
import { useToast } from "../ui/Toast";
import { useSpaceStore } from "../../stores/spaceStore";

interface AddSurfaceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSurfaceDialog({ open, onClose, onSuccess }: AddSurfaceDialogProps) {
  const [surfaceType, setSurfaceType] = React.useState<string>("walls");
  const [maskFiles, setMaskFiles] = React.useState<File[]>([]);
  const [maskPreviews, setMaskPreviews] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [targetDimensions, setTargetDimensions] = React.useState<{ width: number; height: number } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { addToast } = useToast();
  const { originalImage, spaceId } = useSpaceStore();
  // "stairs" splits one staircase mask automatically; the two single-piece
  // types add one tread or riser from a mask drawn by hand, for when the
  // automatic split leaves artifacts.
  const isStairs = surfaceType === "stairs";
  const singleStairsPiece = surfaceType === "stairs-tread" || surfaceType === "stairs-riser";
  // Walls, treads and risers can be uploaded several at once (one mask each);
  // floor, ceiling and the auto-split staircase take a single mask.
  const allowsMany = surfaceType === "walls" || singleStairsPiece;

  // Switching to a single-mask type keeps only the first selected mask
  React.useEffect(() => {
    if (!allowsMany) {
      setMaskFiles((files) => files.slice(0, 1));
      setMaskPreviews((previews) => previews.slice(0, 1));
    }
  }, [allowsMany]);

  // Load target dimensions from original image when dialog opens
  React.useEffect(() => {
    if (open && originalImage) {
      const img = new Image();
      img.onload = () => {
        setTargetDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = originalImage;
    }
  }, [open, originalImage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, allowsMany ? undefined : 1);
    if (!files.length) return;
    setMaskFiles(files);
    setMaskPreviews([]);
    files.forEach((file, i) => {
      const reader = new FileReader();
      reader.onload = () => {
        setMaskPreviews((prev) => {
          const next = [...prev];
          next[i] = reader.result as string;
          return next;
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Use target dimensions from original image, or fall back to uploaded image size
        const targetWidth = targetDimensions?.width || img.naturalWidth;
        const targetHeight = targetDimensions?.height || img.naturalHeight;

        // Draw image onto canvas, resizing if needed
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }
        // Fill with black background first (for transparency handling)
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Draw the image scaled to target dimensions
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        // Export as PNG without alpha issues
        const dataUrl = canvas.toDataURL("image/png");
        const base64 = dataUrl.split(",")[1];

        // Log if resizing occurred
        if (img.naturalWidth !== targetWidth || img.naturalHeight !== targetHeight) {
          console.log(`Resized mask from ${img.naturalWidth}x${img.naturalHeight} to ${targetWidth}x${targetHeight}`);
        }

        resolve(base64);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!maskFiles.length) {
      addToast("error", "Please select a mask image");
      return;
    }

    setIsLoading(true);
    try {
      const base64s = await Promise.all(maskFiles.map(fileToBase64));
      const base64 = base64s[0];
      const pieceSurface = singleStairsPiece ? "stairs" : surfaceType;
      const isRiser = singleStairsPiece ? surfaceType === "stairs-riser" : undefined;
      const response = isStairs
        ? await addStairs(base64, spaceId || undefined)
        : base64s.length > 1
          ? await addSurfaces(pieceSurface as "walls" | "stairs", base64s, spaceId || undefined, isRiser)
          : await addSurface(pieceSurface, base64, spaceId || undefined, isRiser);

      if (response.status === "SUCCESS") {
        addToast("success", isStairs || base64s.length > 1 ? (response.message || "Surfaces added") : "Surface added successfully");
        onSuccess();
        handleReset();
      } else {
        throw new Error(response.message || "Failed to add surface");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to add surface";
      addToast("error", message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSurfaceType("walls");
    setMaskFiles([]);
    setMaskPreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Add New Surface</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Surface Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Surface Type</label>
            <Select value={surfaceType} onValueChange={setSurfaceType}>
              <SelectTrigger>
                <SelectValue placeholder="Select surface type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="floor">Floor</SelectItem>
                <SelectItem value="walls">Wall</SelectItem>
                <SelectItem value="ceiling">Ceiling</SelectItem>
                <SelectItem value="stairs">Stairs (auto-split)</SelectItem>
                <SelectItem value="stairs-tread">Stairs – tread (one piece)</SelectItem>
                <SelectItem value="stairs-riser">Stairs – riser (one piece)</SelectItem>
              </SelectContent>
            </Select>
            {singleStairsPiece && (
              <p className="text-xs text-muted-foreground">
                Upload the mask of ONE {surfaceType === "stairs-riser" ? "riser (the vertical face of a step)" : "tread (the flat top of a step)"}.
                It is added as the next stairs piece and joins the automatic Stairs marker.
              </p>
            )}
            {isStairs && (
              <p className="text-xs text-muted-foreground">
                Upload ONE mask covering the whole staircase (or just the runner). It is split
                automatically into treads and risers, numbered from the bottom step. Deploy afterwards
                so the visualizer shows the Stairs marker.
              </p>
            )}
          </div>

          {/* Mask Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{allowsMany ? "Mask images (one per surface)" : "Mask Image"}</label>
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {maskFiles.length === 1 && maskPreviews[0] ? (
                <div className="space-y-2">
                  <img
                    src={maskPreviews[0]}
                    alt="Mask preview"
                    className="max-h-48 mx-auto rounded"
                  />
                  <p className="text-sm text-muted-foreground">{maskFiles[0].name}</p>
                </div>
              ) : maskFiles.length > 1 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2">
                    {maskFiles.map((file, i) => (
                      <div key={file.name + i} className="space-y-1">
                        {maskPreviews[i] && <img src={maskPreviews[i]} alt={file.name} className="h-16 w-full object-contain rounded bg-black" />}
                        <p className="truncate text-[10px] text-muted-foreground">{file.name}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">{maskFiles.length} masks — added together, one geometry update</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {allowsMany ? "Click to upload one or several mask images" : "Click to upload mask image"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG with white areas indicating the surface
                  </p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={allowsMany}
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isLoading} disabled={!maskFiles.length}>
              {isStairs ? "Split and add stairs" : maskFiles.length > 1 ? `Add ${maskFiles.length} surfaces` : "Add Surface"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
